/*
 * Запросы модуля идут через общий `request` админки.
 *
 * Раньше здесь лежала своя копия: свой `fetch`, своё чтение токена и свой класс
 * ошибки `IdentityApiError`. Из-за него `toResourceError` не узнавал ошибку
 * (`instanceof ApiError`), и отказ по правам показывался как «не удалось
 * загрузить данные» вместо «раздел недоступен вашей роли». Для карточки
 * клиента, где половина секций видна не каждой роли, это ломает главный
 * сценарий: экран краснеет там, где должен молчать.
 */
import { request } from '../../api';

export type DuplicateKind = 'same_phone' | 'same_vehicle' | 'similar_plate' | 'same_name_vehicle';

export interface DuplicateSide {
  readonly id: string;
  readonly displayName: string | null;
  readonly phoneMasked: string | null;
  readonly plates: readonly string[];
  readonly visits: number;
  readonly status: string;
}

export interface DuplicateCandidate {
  readonly id: string;
  readonly leftId: string;
  readonly rightId: string;
  readonly left: DuplicateSide;
  readonly right: DuplicateSide;
  readonly kind: DuplicateKind;
  readonly score: number;
  readonly status: string;
  readonly needsHumanReview: boolean;
  readonly evidence: Record<string, unknown> | null;
  readonly siteIds: readonly string[];
  readonly createdAt: string;
}

export interface DuplicatesPage {
  readonly candidates: readonly DuplicateCandidate[];
  readonly humanOnlyKinds: readonly string[];
  readonly nextCursor: string | null;
}

export function listDuplicates(kind?: DuplicateKind, siteId?: string): Promise<DuplicatesPage> {
  const params = new URLSearchParams();
  if (kind) params.set('type', kind);
  if (siteId) params.set('site_id', siteId);
  const qs = params.toString();
  return request<DuplicatesPage>('GET', `/api/v1/identity/duplicates${qs ? `?${qs}` : ''}`);
}

export interface ScanResult {
  readonly scanId: string;
  readonly proposed: number;
  readonly strengthened: number;
  readonly byKind: Readonly<Record<string, number>>;
}

export function scanDuplicates(): Promise<ScanResult> {
  return request<ScanResult>('POST', '/api/v1/identity/duplicates/scan', {});
}

export interface ResolveItem {
  readonly candidateId: string;
  readonly action: 'merge' | 'reject';
  readonly primaryId?: string;
}

export function resolveDuplicates(
  items: readonly ResolveItem[],
  reason: string,
): Promise<{ resolved: readonly unknown[] }> {
  return request('POST', '/api/v1/identity/duplicates/resolve', { items, reason });
}

export interface ConsentRecord {
  readonly id: string;
  readonly kind: 'data_processing' | 'marketing';
  readonly granted: boolean;
  readonly active: boolean;
  readonly grantedAt: string | null;
  readonly withdrawnAt: string | null;
  readonly textVersion: string | null;
  readonly channel: string | null;
}

export interface CustomerSide {
  readonly id: string;
  readonly displayName: string | null;
  readonly phone: string | null;
  readonly status: string;
  readonly createdAt: string;
  readonly phones: readonly string[];
  readonly vehicles: ReadonlyArray<{ id: string; plate: string; role: string; active: boolean }>;
  readonly visitCount: number;
  readonly lastVisitAt: string | null;
  readonly paidMinor: number;
  readonly balanceMinor: number;
  readonly feedbackCount: number;
  readonly consents: readonly ConsentRecord[];
  readonly merges: ReadonlyArray<{
    mergeId: string;
    primaryId: string;
    mergedId: string;
    reason: string;
    mergedAt: string;
    revertedAt: string | null;
  }>;
}

export interface ComparePayload {
  readonly left: CustomerSide;
  readonly right: CustomerSide;
}

export function compareCustomers(leftId: string, rightId: string): Promise<ComparePayload> {
  return request<ComparePayload>(
    'GET',
    `/api/v1/identity/customers/${encodeURIComponent(leftId)}/compare?with=${encodeURIComponent(rightId)}`,
  );
}

export function mergeCustomers(
  primaryId: string,
  sourceId: string,
  reason: string,
): Promise<{ mergeId: string }> {
  return request('POST', '/api/v1/identity/merge', { primaryId, sourceId, reason });
}

export function splitCustomers(mergeId: string, reason: string): Promise<unknown> {
  return request('POST', '/api/v1/identity/split', { mergeId, reason });
}

export interface ConflictView {
  readonly vehicleId: string;
  readonly plate: string;
  readonly currentCustomerId: string | null;
  readonly currentCustomerName: string | null;
  readonly currentLinkId: string | null;
  readonly since: string | null;
  readonly comparison: ComparePayload | null;
}

export function describeConflict(vehicleId: string, challengerId?: string): Promise<ConflictView> {
  const qs = challengerId ? `?customer_id=${encodeURIComponent(challengerId)}` : '';
  return request<ConflictView>(
    'GET',
    `/api/v1/identity/conflicts/${encodeURIComponent(vehicleId)}${qs}`,
  );
}

export function resolveConflict(
  vehicleId: string,
  keepCustomerId: string,
  reason: string,
): Promise<unknown> {
  return request('POST', '/api/v1/identity/conflicts/resolve', {
    vehicleId,
    keepCustomerId,
    reason,
  });
}

export interface ConsentsPayload {
  readonly current: Record<string, { active: boolean; grantedAt: string | null; textVersion: string | null }>;
  readonly history: readonly ConsentRecord[];
}

export function customerConsents(customerId: string): Promise<ConsentsPayload> {
  return request<ConsentsPayload>(
    'GET',
    `/api/v1/identity/customers/${encodeURIComponent(customerId)}/consents`,
  );
}

export function recordConsent(
  customerId: string,
  kind: 'data_processing' | 'marketing',
  granted: boolean,
  textVersion: string,
): Promise<ConsentRecord> {
  return request('POST', `/api/v1/identity/customers/${encodeURIComponent(customerId)}/consents`, {
    kind,
    granted,
    textVersion,
    channel: 'web',
  });
}

export function withdrawConsent(
  customerId: string,
  reason: string,
  anonymize: boolean,
  kind?: 'data_processing' | 'marketing',
): Promise<unknown> {
  return request('POST', `/api/v1/identity/customers/${encodeURIComponent(customerId)}/withdraw`, {
    reason,
    anonymize,
    ...(kind ? { kind } : {}),
  });
}

export interface SegmentSummary {
  readonly id: string;
  readonly code: string;
  readonly name: string;
  readonly definition: unknown;
  readonly refreshMode: 'live' | 'scheduled';
  readonly lastSize: number | null;
  readonly lastBuiltAt: string | null;
  readonly status: string;
}

export function listSegments(): Promise<{ segments: readonly SegmentSummary[] }> {
  return request('GET', '/api/v1/identity/segments');
}

export function previewSegment(definition: unknown): Promise<{ size: number }> {
  return request('POST', '/api/v1/identity/segments/preview', { definition });
}

export function createSegment(input: {
  code: string;
  name: string;
  definition: unknown;
  refreshMode: 'live' | 'scheduled';
  intervalMinutes?: number;
}): Promise<SegmentSummary> {
  return request('POST', '/api/v1/identity/segments', input);
}

export interface MemberCard {
  readonly id: string;
  readonly displayName: string | null;
  /** Замаскирован, если у роли нет права видеть ПДн. */
  readonly phone: string | null;
  readonly status: string;
  readonly visitCount: number;
  readonly lastVisitAt: string | null;
  readonly loyaltyTier: string | null;
}

export interface MembersPage {
  readonly customerIds: readonly string[];
  /** Карточки в том же порядке: по ним сегмент проверяют глазами. */
  readonly members: readonly MemberCard[];
  readonly asOf: string;
  readonly mode: string;
  readonly nextCursor: string | null;
}

export function segmentMembers(id: string): Promise<MembersPage> {
  return request<MembersPage>('GET', `/api/v1/identity/segments/${encodeURIComponent(id)}/members`);
}

export function updateSegment(
  id: string,
  input: {
    name?: string;
    definition?: unknown;
    refreshMode?: 'live' | 'scheduled';
    intervalMinutes?: number;
  },
): Promise<SegmentSummary> {
  return request('PATCH', `/api/v1/identity/segments/${encodeURIComponent(id)}`, input);
}

/** Удаление — перевод в архив: по сегменту уже могли уйти рассылки. */
export function archiveSegment(id: string): Promise<SegmentSummary> {
  return request('DELETE', `/api/v1/identity/segments/${encodeURIComponent(id)}`);
}

export function refreshSegment(id: string): Promise<unknown> {
  return request('POST', `/api/v1/identity/segments/${encodeURIComponent(id)}/refresh`);
}

export interface MergeHistoryRow {
  readonly mergeId: string;
  readonly primaryId: string;
  readonly primaryName: string | null;
  readonly primaryPhoneMasked: string | null;
  readonly mergedId: string;
  readonly mergedName: string | null;
  readonly mergedPhoneMasked: string | null;
  readonly reason: string;
  readonly mergedAt: string;
  readonly revertedAt: string | null;
  readonly canSplit: boolean;
  readonly moved: Readonly<Record<string, number>>;
}

export function listMerges(customerId?: string): Promise<{ merges: readonly MergeHistoryRow[] }> {
  const qs = customerId ? `?customer_id=${encodeURIComponent(customerId)}` : '';
  return request<{ merges: readonly MergeHistoryRow[] }>('GET', `/api/v1/identity/merges${qs}`);
}

/* ---------------------------------------- справочник полей конструктора */

export type FieldGroup = 'behaviour' | 'customer' | 'vehicle' | 'loyalty';

export interface FieldOption {
  readonly value: string;
  readonly titleRu: string;
  readonly titleKk: string;
}

export interface SegmentField {
  readonly id: string;
  readonly group: FieldGroup;
  readonly hintRu: string;
  readonly hintKk: string;
  /** Готовые значения. Пусто — поле не перечислимое. */
  readonly options?: readonly FieldOption[];
}

export interface FieldCatalog {
  readonly groups: readonly { readonly id: FieldGroup; readonly titleRu: string; readonly titleKk: string }[];
  readonly fields: readonly SegmentField[];
}

/**
 * Что можно положить в условие сегмента.
 *
 * Список приходит с сервера: значения объектов, уровней лояльности и марок
 * живут в базе, и держать их копию в интерфейсе значит разойтись при первом же
 * новом объекте.
 */
export function segmentFields(): Promise<FieldCatalog> {
  return request('GET', '/api/v1/identity/segments/fields');
}
